<script setup>
import { computed } from 'vue';

const props = defineProps({
  question: { type: String, required: true },
  helpfulLabel: { type: String, required: true },
  unhelpfulLabel: { type: String, required: true },
  reportLabel: { type: String, required: true },
  helpfulCount: { type: Number, default: 0 },
  unhelpfulCount: { type: Number, default: 0 },
  selectedReaction: { type: String, default: '' },
  submitting: { type: Boolean, default: false },
  reasonsExpanded: { type: Boolean, default: false },
  accent: { type: String, default: 'brand' },
  testIdPrefix: { type: String, default: '' },
});

const emit = defineEmits(['helpful', 'unhelpful', 'report']);

const hasSelection = computed(() => Boolean(props.selectedReaction));
const neutralClasses =
  'border-n-slate-3 bg-n-alpha-1 text-n-slate-12 dark:border-n-slate-7 dark:bg-n-alpha-2';
const accentHoverClasses = computed(() =>
  props.accent === 'widget'
    ? 'hover:border-[var(--widget-color)] hover:text-[var(--widget-color)]'
    : 'hover:border-n-brand hover:text-n-brand'
);
const testId = action =>
  props.testIdPrefix ? `${props.testIdPrefix}-${action}` : undefined;
</script>

<template>
  <div
    class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
  >
    <p class="mb-0 text-sm font-semibold text-n-slate-12">
      {{ question }}
    </p>
    <div class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        :data-testid="testId('helpful')"
        class="inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        :class="[
          selectedReaction === 'helpful'
            ? '!border-n-grass-9 !bg-n-grass-9 !text-white'
            : [neutralClasses, accentHoverClasses],
          { 'pointer-events-none': hasSelection },
        ]"
        :disabled="submitting"
        :aria-pressed="selectedReaction === 'helpful'"
        :aria-disabled="hasSelection"
        @click="emit('helpful')"
      >
        <span class="i-lucide-thumbs-up size-4" aria-hidden="true" />
        {{ helpfulLabel }}
        <span
          v-if="helpfulCount > 0"
          class="rounded-full bg-n-alpha-2 px-1.5 text-xs leading-5 text-current"
          data-testid="feedback-helpful-count"
        >
          {{ helpfulCount }}
        </span>
      </button>
      <button
        type="button"
        :data-testid="testId('unhelpful')"
        class="inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        :class="[
          selectedReaction === 'unhelpful'
            ? '!border-n-ruby-9 !bg-n-ruby-9 !text-white'
            : [neutralClasses, accentHoverClasses],
          { 'pointer-events-none': hasSelection },
        ]"
        :disabled="submitting"
        :aria-pressed="selectedReaction === 'unhelpful'"
        :aria-expanded="reasonsExpanded"
        :aria-disabled="hasSelection"
        @click="emit('unhelpful')"
      >
        <span class="i-lucide-thumbs-down size-4" aria-hidden="true" />
        {{ unhelpfulLabel }}
        <span
          v-if="unhelpfulCount > 0"
          class="rounded-full bg-n-alpha-2 px-1.5 text-xs leading-5 text-current"
          data-testid="feedback-unhelpful-count"
        >
          {{ unhelpfulCount }}
        </span>
      </button>
      <button
        type="button"
        :data-testid="testId('report')"
        class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-slate-3 bg-n-alpha-1 px-3 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-ruby-7 hover:text-n-ruby-11 disabled:cursor-not-allowed disabled:opacity-60 dark:border-n-slate-7 dark:bg-n-alpha-2"
        :disabled="submitting || hasSelection"
        @click="emit('report')"
      >
        <span class="i-lucide-flag size-4" aria-hidden="true" />
        {{ reportLabel }}
      </button>
    </div>
  </div>
</template>
